import React, { FC, ReactNode, useEffect, useState } from 'react';
import { CustomerTemplate } from '../types';
import { FieldData, SignatureField, TemplateData, useCustomerTemplates } from "../hooks/customer";

import { makeStyles }    from "@material-ui/core/styles";
import Grid              from "@material-ui/core/Grid";
import FormControl       from "@material-ui/core/FormControl";
import InputLabel        from "@material-ui/core/InputLabel";
import Select            from "@material-ui/core/Select";
import MenuItem          from "@material-ui/core/MenuItem";
import Typography        from "@material-ui/core/Typography";
import TextField         from "@material-ui/core/TextField";
import IconButton        from '@material-ui/core/IconButton';
import Button            from '@material-ui/core/Button';
import Dialog            from '@material-ui/core/Dialog';
import DialogActions     from '@material-ui/core/DialogActions';
import DialogContent     from '@material-ui/core/DialogContent';
import DialogTitle       from '@material-ui/core/DialogTitle';

import DeleteIcon from '@material-ui/icons/Delete';
import UndoIcon from '@material-ui/icons/Undo';
import AddIcon  from '@material-ui/icons/Add';
import ArrowUpIcon  from '@material-ui/icons/ArrowUpward';
import ArrowDownIcon  from '@material-ui/icons/ArrowDownward';

import { FormattedMessage, useIntl } from 'react-intl';
import { useTranslations }           from "../providers/translations-provider";
import { FormHelperText }            from "@material-ui/core";

export interface TemplateSelectorProps {
    templates: CustomerTemplate[];
    onTemplateChange: (template: CustomerTemplate | null) => void;
    onFieldDataChange: (data: FieldData) => void;
    onSignaturesChange: (signatures: SignatureField[]) => void;
    onTemplateLanguageChange: (language: string) => void;
}

const useStyles = makeStyles(theme => ({
    formControl: {
        marginTop: 24,
        flexGrow: 1
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
    separator: {
        marginTop: 48
    },
    topButtonWrapper: {
        marginTop: 10,
        position: 'relative'
    },
    topButton: {
        position: 'absolute',
        right: 10,
        top: 20
    },
    signatureButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'flex-end'
    },
    horizontalFlex: {
        display: 'flex',
        flexDirection: 'column'
    }
}));

export interface WarningActionWrapper {
    performAction: () => void,
    title: ReactNode;
    content: ReactNode;
}

const formatMessageLength = (fields: FieldData, f: TemplateData) => {

    return `${ (f.maxLength && fields[f.key] && fields[f.key].value) ? fields[f.key].value.length : 0 }/${ f.maxLength }`;
}

export const TemplateSelector: FC<TemplateSelectorProps> = ({ templates: t, onFieldDataChange, onTemplateChange, onSignaturesChange, onTemplateLanguageChange }) => {
    const classes = useStyles();

    const {
        templates,
        languages,
        selectedLanguage,
        setLanguage,
        selectedTemplate,
        setTemplate,
        templateData,
        fields,
        setField,
        isFieldsDirty,
        signatures,
        addSignature,
        removeSignature,
        updateSignature,
        swapSignature
    } = useCustomerTemplates(t);

    const intl = useIntl();
    const  {currentLanguage}  = useTranslations();

    const [warningAction, setActionWithWarning] = useState<WarningActionWrapper | null>(null);

    const handleResetDialogClose = (execute: boolean) => () => {
        if (execute && warningAction) {
            warningAction.performAction();
            setActionWithWarning(null);
        } else {
            setActionWithWarning(null);
        }
    }

    const callIfNotDirty = (cb: (event: any) => void) => (event: any) => {
        if (isFieldsDirty) {
            setActionWithWarning({
                performAction: () => {
                    cb(event);
                },
                title: intl.formatMessage({id: 'inputs.dialog.reset.title'}),
                content: <FormattedMessage id={ 'inputs.dialog.reset.content' }/>
            });
        } else {
            cb(event);
        }
    }

    const resetInputField = (key: string) => () => {
        const templateField = templateData?.find(i => i.key === key);

        if (templateField) {
            const translatedName = templateField.name[currentLanguage];
            setActionWithWarning({
                performAction: () => {
                    setField(key)({ target: { value: templateField.defaultValue } });
                },
                title: <FormattedMessage id={ 'inputs.dialog.reset-field.title' }
                                         values={ { fieldName: translatedName } }/>,
                content: <div>
                    <FormattedMessage id={ 'inputs.dialog.reset-field.content' }/>:
                    <p>
                        { templateField.defaultValue }
                    </p>
                </div>
            })
        }
    }

    useEffect(() => {
        onTemplateChange(selectedTemplate);
    }, [onTemplateChange, selectedTemplate]);

    useEffect(() => {
        if(Object.keys(fields).length > 0) {
            onFieldDataChange(fields);
        }
    }, [fields, onFieldDataChange]);

    useEffect(() => {
        onSignaturesChange(signatures);
    }, [signatures]);

    useEffect(() => {
        onTemplateLanguageChange(selectedLanguage);
    }, [selectedLanguage]);

    const hasMultipleLanguages = (selectedTemplate && selectedTemplate.languages.length > 1) ?? false;
    const firstStep = templates.length > 1 ? 1 : 0;
    const hideSignatureSubtitle = (selectedTemplate?.signatureInfo?.hideSubtitle !== true);
    const maxSignatureHint = selectedTemplate?.signatureInfo?.maxSignatures ? `(${signatures.length} / ${selectedTemplate.signatureInfo.maxSignatures})` : '';

    return <>
        <Dialog open={ warningAction !== null } onClose={ handleResetDialogClose(false) }>
            <DialogTitle>
                { warningAction?.title }
            </DialogTitle>
            <DialogContent>
                { warningAction?.content }
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={ handleResetDialogClose(false) }
                    color="primary">
                    <FormattedMessage id={ 'inputs.dialog.abort' }/>
                </Button>
                <Button
                    onClick={ handleResetDialogClose(true) }
                    variant="contained"
                    color="secondary"
                    autoFocus
                >
                    <FormattedMessage id={ 'inputs.dialog.ok' }/>
                </Button>
            </DialogActions>
        </Dialog>
        {
            templates.length > 1 &&
            <Grid container>
                <FormControl className={ classes.formControl }>
                    <InputLabel shrink>
                        { firstStep }. <FormattedMessage id={ 'inputs.steps.template' }/>
                    </InputLabel>
                    <Select
                        displayEmpty
                        className={ classes.selectEmpty }
                        onChange={ callIfNotDirty(setTemplate) }
                        value={ selectedTemplate ? selectedTemplate.key : '' }
                    >
                        {
                            templates.map(t => <MenuItem
                                key={ t.key }
                                value={ t.key }>{ t.name[currentLanguage] }
                            </MenuItem>)
                        }
                    </Select>
                </FormControl>
            </Grid>
        }
        {
            hasMultipleLanguages &&
            <Grid container>
                <FormControl className={ classes.formControl }>
                    <InputLabel shrink>
                        { firstStep + 1 }. <FormattedMessage id={ 'inputs.steps.language' }/>
                    </InputLabel>
                    <Select
                        displayEmpty
                        className={ classes.selectEmpty }
                        onChange={ callIfNotDirty(setLanguage) }
                        value={ selectedLanguage }
                    >
                        {
                            languages.map(t => (
                                <MenuItem
                                    key={ t.key }
                                    value={ t.key }>{ t.name }
                                </MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
            </Grid>
        }
        {
            templateData &&
            <Grid className={ classes.separator }>
                <Typography variant={ 'h6' }>
                    <FormattedMessage id={ 'inputs.fields' }/>
                </Typography>
                {
                    templateData.filter(f => f.info.hide !== true).map(( f, index) => {
                        const errors = fields[f.key] ? fields[f.key].errors : [];
                        const hasError = fields[f.key] ? !fields[f.key].valid : false;
                        return <Grid container key={ f.key }>
                            <FormControl className={ classes.formControl }>
                                <TextField
                                    label={ `${ firstStep + index + (hasMultipleLanguages ? 2 : 1) }. ${ intl.formatMessage({ id: 'inputs.text-field' }) }: ${ f.name[currentLanguage] }` }
                                    multiline
                                    variant={ 'outlined' }
                                    rows={ 2 }
                                    rowsMax={ 10 }
                                    value={ fields[f.key] ? fields[f.key].value : '' }
                                    onChange={ setField(f.key) }
                                    error={hasError}
                                />
                                <FormHelperText error={hasError}>
                                    {formatMessageLength(fields, f)}<br/>
                                    {
                                        errors.map((err, errorIndex) => <div key={errorIndex}>
                                            <FormattedMessage id={err.message} defaultMessage={err.message} values={err.intlData}/>
                                        </div>)
                                    }
                                </FormHelperText>
                            </FormControl>
                            <div className={ classes.topButtonWrapper }>
                                <IconButton
                                    aria-label="undo"
                                    size={ 'small' }
                                    className={ classes.topButton }
                                    onClick={ resetInputField(f.key) }
                                    disabled={ fields[f.key] ? (!fields[f.key].dirty) : true }
                                >
                                    <UndoIcon/>
                                </IconButton>
                            </div>
                        </Grid>
                    })
                }
            </Grid>
        }
        {
            (templateData && selectedTemplate && selectedTemplate.signatureInfo) &&
            <Grid className={ classes.separator }>

                <Typography variant={'h6'}>
                    <FormattedMessage
                        id={'inputs.signatures'}/> {maxSignatureHint}
                </Typography>
                <div className={ classes.signatureButton }>
                    <IconButton
                        aria-label="undo"
                        size={ 'medium' }
                        onClick={ addSignature }
                        disabled={ !!selectedTemplate?.signatureInfo && !!selectedTemplate?.signatureInfo?.maxSignatures && signatures.length >= selectedTemplate?.signatureInfo?.maxSignatures }
                    >
                        <AddIcon />
                    </IconButton>
                </div>
                {
                    signatures.map((signature, index) => {
                        const nextId = signatures[index+1] ? signatures[index+1].id : -1;
                        const prevId = signatures[index-1] ? signatures[index-1].id : -1;
                        return <Grid container spacing={3} key={ signature.id } justify={'center'} alignItems={'center'}>
                            <Grid item xs={hideSignatureSubtitle ? 5 : 10}>
                                <TextField
                                    className={ classes.formControl }
                                    label={ intl.formatMessage({ id: 'inputs.signatureText' }) }
                                    variant={ 'standard' }
                                    value={ signature.signature }
                                    onChange={ updateSignature(signature.id)('signature') }
                                    error={signature.signature.trim().length === 0}
                                />
                              { selectedTemplate?.signatureInfo?.maxLength &&
                                  <FormHelperText>
                                    {`${signature.signature.length}/${selectedTemplate?.signatureInfo?.maxLength}`}
                                  </FormHelperText>
                              }
                            </Grid>
                            {
                                hideSignatureSubtitle &&
                                <Grid item xs={5}>
                                    <TextField
                                        className={ classes.formControl }
                                        label={ intl.formatMessage({ id: 'inputs.signatureSubText' }) }
                                        variant={ 'standard' }
                                        value={ signature.subtitle }
                                        onChange={ updateSignature(signature.id)('subtitle') }
                                    />
                                  { selectedTemplate?.signatureInfo?.maxLength &&
                                      <FormHelperText>
                                          {`${signature.signature.length}/${selectedTemplate?.signatureInfo?.maxLength}`}
                                      </FormHelperText>
                                  }
                                </Grid>
                            }
                            <Grid item xs={1}>
                                <div className={classes.horizontalFlex}>
                                    <IconButton
                                        size={ 'small' }
                                        disabled={index === 0}
                                        onClick={() => swapSignature(signature.id, prevId)}
                                    >
                                        <ArrowUpIcon/>
                                    </IconButton>
                                    <IconButton
                                        size={ 'small' }
                                        disabled={index === signatures.length -1}
                                        onClick={() => swapSignature(signature.id, nextId)}
                                    >
                                        <ArrowDownIcon/>
                                    </IconButton>
                                </div>
                            </Grid>
                            <Grid item xs={1}>
                                <IconButton
                                    size={ 'small' }
                                    onClick={() => removeSignature(signature.id)}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                            </Grid>
                        </Grid>
                    })
                }
            </Grid>
        }
    </>;
}